/**
 * Client for the group-trip carpool flow (backend: carpool_service.py).
 *
 * Drives the whole carpool group rather than a single rider: one "Start Ride"
 * that notifies every accepted passenger, an optimized pickup route, a common
 * pickup point, passenger attendance, cancellation (with automatic
 * re-optimization) and SOS.
 *
 * Auth is added globally by the axios request interceptor in App.js, so these
 * helpers don't take a token.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

/** Driver: optimized pickup plan for a date (driver -> A -> B -> ... -> office). */
export const getTripPlan = async (date) => {
  const res = await axios.get(`${API}/carpool/trip?date=${encodeURIComponent(date)}`);
  return res.data;
};

/** Driver: start the whole trip; the backend notifies every accepted passenger. */
export const startTrip = async (date, destination) => {
  const res = await axios.post(`${API}/carpool/trip/start`, { date, destination });
  return res.data;
};

/** Driver: set a shared meeting point; passengers get the navigation link + ETA. */
export const setCommonPickup = async (date, label, latitude, longitude) => {
  const res = await axios.post(`${API}/carpool/trip/common-pickup`, {
    date,
    label,
    latitude,
    longitude,
  });
  return res.data;
};

/** Passenger: confirm boarding (attendance record). */
export const confirmBoarding = async (requestId) => {
  const res = await axios.post(`${API}/carpool/requests/${requestId}/confirm-boarding`);
  return res.data;
};

/** Passenger: cancel before pickup; the driver's route is re-optimized automatically. */
export const cancelRequest = async (requestId) => {
  const res = await axios.post(`${API}/carpool/requests/${requestId}/cancel`);
  return res.data;
};

/** Either party: raise an SOS during a ride. */
export const raiseSos = async ({ requestId, latitude, longitude }) => {
  const res = await axios.post(`${API}/carpool/sos`, {
    request_id: requestId,
    latitude,
    longitude,
  });
  return res.data;
};

/** Set the user's emergency contacts (user ids alerted on SOS). */
export const setEmergencyContacts = async (contactIds) => {
  const res = await axios.put(`${API}/carpool/emergency-contacts`, { contact_ids: contactIds });
  return res.data;
};

/** Completed carpool ride log (as driver or passenger). */
export const getRideHistory = async () => {
  const res = await axios.get(`${API}/carpool/history`);
  return res.data;
};
